import i18n from 'i18next';
import {Box, Center, HStack, Show, ThemeIconKey, VStack} from 'platform/foundation';
import {RcFile, UploadRequestError} from 'rc-upload/es/interface';

import {MouseEventHandler, MouseEvent as ReactMouseEvent, useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {buildArray, suffixTestId, TestIdProps} from 'shared/web';

import {FormControlProps} from '../../types/FormControlProps';
import {closeDialog} from '../Dialog/utils/closeDialog';
import {openDialog} from '../Dialog/utils/openDialog';
import {HelperText} from '../HelperText/HelperText';
import {Preview, PreviewAction} from '../Preview/Preview';
import {OnBeforeUpload, OnUploadError, OnUploadSuccess, Upload} from '../Upload/Upload';
import {UploadState} from '../Upload/UploadState';
import {ImageCropper} from './components/ImageCropper';

export type UploadImageValue = {
  url: string;
  id: string;
};

export type UploadImageProps = FormControlProps<UploadImageValue | null> &
  TestIdProps & {
    helperText?: string;
    uploadFileRequest: (file: File) => Promise<UploadImageValue>;
    srcValueFormatter?: (url: string) => string;
    linkValueFormatter?: (url: string) => string;
    onPreviewClick?: (url: string, event: ReactMouseEvent<HTMLButtonElement, MouseEvent>) => void;
    onDeleteClick?: MouseEventHandler<HTMLButtonElement>;
    onUploadError?: OnUploadError;
    onUploadStart?: (file: RcFile) => void;
    onUploadSuccess?: OnUploadSuccess;
    beforeUpload?: OnBeforeUpload;
    uploadText?: string;
    uploadingText?: string;
    uploadIcon?: ThemeIconKey;
    uploadingIcon?: ThemeIconKey;
    uploadErrorIcon?: ThemeIconKey;
    size?: 'minHeight' | 'default';
    previewHeight?: 'auto';
    previewWidth?: 'auto';
    aspectRatio?: `${number}/${number}`;
    resolution?: {width: number; height: number};
  };

export function UploadImage(props: UploadImageProps) {
  const {value, onChange, uploadFileRequest} = props;

  const handleChange = (uploadData?: UploadImageValue) => onChange?.(uploadData ?? null);
  const [uploadState, setUploadState] = useState<UploadState>(UploadState.Idle);

  const handleDeleteClick = (event: ReactMouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (props.onDeleteClick) {
      return props.onDeleteClick(event);
    }
    handleChange();
  };

  const handlePreviewClick = (event: ReactMouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (props.onPreviewClick && value) {
      props.onPreviewClick(value.url, event);
    }
  };

  const uploadFile = async (file: File, onError?: (error: UploadRequestError) => void) =>
    await uploadFileRequest(file)
      .then(handleChange)
      .then(() => setUploadState(UploadState.Success))
      .catch((err) => {
        onError?.(err as UploadRequestError);
        setUploadState(UploadState.Error);
      });

  const handleRequest = async (file: File, onError?: (error: UploadRequestError) => void) => {
    setUploadState(UploadState.Uploading);

    if (isNil(props.aspectRatio) && isNil(props.resolution)) {
      return await uploadFile(file, onError);
    }

    openDialog(
      <ImageCropper
        file={file}
        aspectRatio={props.aspectRatio}
        resolution={props.resolution}
        onSubmit={async (croppedFile) => await uploadFile(croppedFile, onError)}
        onCloseDialog={() => closeDialog('cropImage')}
      />,
      {
        title: i18n.t('general.labels.cropImage'),
        disableBodyPadding: true,
        onCloseComplete: () => setUploadState(UploadState.Idle),
        id: 'cropImage',
      }
    );
  };

  return (
    <VStack>
      <HStack>
        <Box data-testid={suffixTestId('uploadImageWrapper', props)} width="100%" height="100%">
          <Center height="100%">
            {value ? (
              <HStack
                align="center"
                height={props.previewHeight ?? 27}
                width={props.previewWidth ?? 27}
              >
                <Preview
                  data-testid={suffixTestId('uploadImagePreview', props)}
                  url={props.srcValueFormatter?.(value.url) ?? value.url}
                  height={props.previewHeight ?? 27}
                  width={props.previewWidth ?? 27}
                  makeUrl={() => props.srcValueFormatter?.(value.url) ?? value.url}
                  shouldUseOriginal
                  fit="contain"
                  actions={buildArray<PreviewAction>()
                    .when(isNotNil(props.onPreviewClick), {
                      icon: 'action/visibility',
                      onClick: handlePreviewClick,
                    })
                    .add({
                      icon: 'action/delete',
                      isDisabled: props.isDisabled,
                      onClick: handleDeleteClick,
                    })}
                />
              </HStack>
            ) : (
              <Upload
                data-testid={suffixTestId('upload', props)}
                type="card"
                customRequest={({file, onError}) => {
                  const typedFile = file as File;

                  handleRequest(typedFile, onError);
                }}
                isDisabled={props.isDisabled}
                accept="image/png, image/jpg, image/jpeg"
                shouldValidateAccept
                size={props.size}
                uploadState={uploadState}
                onStart={props.onUploadStart}
                onSuccess={props.onUploadSuccess}
                onError={props.onUploadError}
                beforeUpload={props.beforeUpload}
                uploadText={props.uploadText}
                uploadingText={props.uploadingText}
                uploadIcon={props.uploadIcon}
                uploadingIcon={props.uploadingIcon}
                errorIcon={props.uploadErrorIcon}
              />
            )}
          </Center>
        </Box>
      </HStack>
      <Show when={props.errorMessage ?? props.helperText}>
        <HelperText
          errorMessage={props.errorMessage}
          helperText={props.helperText}
          data-testid={suffixTestId('helper', props)}
        />
      </Show>
    </VStack>
  );
}
